'use client';

import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import HeroActivity from './HeroActivity';
import { useSiteLanguage } from '@/components/common/SiteLanguageProvider';

export default function HeroSection() {
  const { language } = useSiteLanguage();

  // One headline, one short line: visitors scan, they do not read paragraphs.
  const copy = {
    en: {
      kineticLead: 'Digitize your',
      kineticWords: ['bookings', 'payments', 'clients', 'appointments'],
      kineticTail: 'without the hassle.',
      srHeading: 'Digitize your bookings, payments, clients and appointments, without the hassle.',
      valueProp: 'All on your phone, live in a few weeks.',
      primaryCta: 'Start your project',
      secondaryCta: 'View our work',
    },
    fr: {
      kineticLead: 'Numérisez vos',
      kineticWords: ['réservations', 'paiements', 'clients', 'rendez-vous'],
      kineticTail: 'sans complexité.',
      srHeading: 'Numérisez vos réservations, paiements, clients et rendez-vous, sans complexité.',
      valueProp: 'Tout sur votre téléphone, en quelques semaines.',
      primaryCta: 'Commencer votre projet',
      secondaryCta: 'Voir notre travail',
    },
  } as const;

  const t = copy[language];

  return (
    <section className="relative lg:min-h-[100dvh] flex items-center justify-center bg-background overflow-hidden pt-28 pb-16 sm:pt-32 sm:pb-20 lg:pt-32 lg:pb-20">
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-secondary/5" />
      <div className="absolute inset-0 hidden lg:block opacity-20" aria-hidden="true">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center">
          {/* Left: Text Content */}
          <div className="text-center lg:text-left">
            {/* Kinetic headline: the last word flips through what we digitize (CSS only, see .kinetic-* in marketing-theme.css) */}
            <h1 className="font-display text-hero mb-4 sm:mb-6 text-foreground animate-fade-in">
              <span className="sr-only">{t.srHeading}</span>
              <span aria-hidden="true">
                <span className="block">{t.kineticLead}</span>
                <span className="kinetic-window block">
                  <span className="kinetic-strip block">
                    {[...t.kineticWords, t.kineticWords[0]].map((word, index) => (
                      <span key={`${word}-${index}`} className="kinetic-word block text-primary">
                        {word}
                      </span>
                    ))}
                  </span>
                </span>
                <span className="mt-2 block text-[0.55em] leading-tight tracking-[-0.01em] text-muted-foreground">{t.kineticTail}</span>
              </span>
            </h1>
            <p className="font-sans text-supporting-lg mb-6 sm:mb-8 max-w-3xl mx-auto lg:mx-0 text-foreground">
              {t.valueProp}
            </p>
            <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 sm:gap-4 justify-center lg:justify-start">
              <Link
                href="/contact"
                className="whitespace-nowrap px-6 sm:px-8 py-3 sm:py-4 bg-primary text-primary-foreground font-medium rounded-sm hover:bg-primary/90 active:scale-[0.98] transition-[background-color,transform] duration-200 inline-flex items-center justify-center gap-2 text-nav"
              >
                {t.primaryCta}
                <Icon name="ArrowRightIcon" size={20} />
              </Link>
              <Link
                href="/work"
                className="view-work-glow-button whitespace-nowrap px-6 sm:px-8 py-3 sm:py-4 text-primary font-medium rounded-sm active:scale-[0.98] transition-[border-color,box-shadow,transform] duration-200 inline-flex items-center justify-center gap-2 text-nav relative z-[1]"
              >
                {t.secondaryCta}
              </Link>
            </div>
          </div>

          {/* Live-activity card: below the CTAs on phones, on the right on desktop */}
          <div className="relative w-full mt-2 lg:mt-0">
            <HeroActivity language={language} />
          </div>
        </div>
      </div>
    </section>
  );
}
